import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, ChevronUp, X } from 'lucide-react';

/**
 * Multi-select combobox for long or grouped lists.
 *
 * Combines a searchable dropdown with checkbox selection, optional grouping,
 * and removable chips for selected items.
 *
 * value = [] means "none selected" (unlike MultiPillSelect where [] = all).
 * Use allLabel prop to enable a "Select All" / "All X" checkbox at the top.
 *
 * Variants:
 *   pill (default) → rounded-full trigger at 44px (h-11), matching PillSelect
 *   form           → full-width input-style trigger. Menu is always portaled
 *                    (modal-safe) — same contract as FormSelect / SearchableCombobox.
 *
 * @example
 * <SearchableMultiCombobox
 *   items={workers}
 *   value={selectedIds}
 *   onChange={setSelectedIds}
 *   displayFn={w => `${w.first_name} ${w.last_name}`}
 *   valueFn={w => w.staff_id}
 *   searchFn={(w, term) => `${w.first_name} ${w.last_name}`.toLowerCase().includes(term)}
 *   placeholder="Select workers..."
 *   searchPlaceholder="Search by name or staff ID..."
 *   icon={Users}
 *   iconColor="text-red-600"
 *   theme="admin"
 *   showChips
 * />
 *
 * Grouped usage:
 * <SearchableMultiCombobox
 *   items={workers}
 *   value={selectedIds}
 *   onChange={setSelectedIds}
 *   groupFn={w => w.trade_category}
 *   ...
 * />
 */
const SearchableMultiCombobox = ({
  items = [],
  value = [],
  onChange,
  displayFn = (item) => String(item),
  valueFn = (item) => item?.id ?? '',
  searchFn,
  groupFn,
  placeholder = 'Select...',
  allLabel,
  searchPlaceholder = 'Search...',
  emptyLabel = 'No results found',
  icon: Icon,
  iconColor = 'text-gray-500',
  theme = 'admin',
  variant = 'pill',
  minWidth = 'min-w-[160px]',
  disabled = false,
  loading = false,
  loadingLabel = 'Loading...',
  showChips = false,
  className = '',
  renderItem,
}) => {
  const isAdmin = theme === 'admin';
  const isPill = variant === 'pill';
  const isFormVariant = variant === 'form';
  // Form/modal contexts must portal (FormSelect + DatePickerField contract).
  const usePortal = isFormVariant;
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownMaxHeight = 320; // max-h-80
    const spaceBelow = viewportHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUpward = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;
    const width = Math.min(rect.width, viewportWidth - 16);
    const left = Math.max(8, Math.min(rect.left, viewportWidth - width - 8));

    setDropdownStyle({
      position: 'fixed',
      left,
      width,
      // Above sticky modal header/footer (10001) — same as DatePickerField
      zIndex: 10002,
      ...(openUpward
        ? { bottom: viewportHeight - rect.top + 4, maxHeight: Math.min(spaceAbove, dropdownMaxHeight) }
        : { top: rect.bottom + 4, maxHeight: Math.min(spaceBelow, dropdownMaxHeight) }),
    });
  }, []);

  // Click-outside only while open; account for the portaled menu node.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (usePortal && menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, usePortal]);

  useEffect(() => {
    if (isOpen && searchRef.current) searchRef.current.focus();
    if (!isOpen) setSearch('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !usePortal) return undefined;
    updateDropdownPosition();
    const update = () => updateDropdownPosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, usePortal, updateDropdownPosition]);

  const defaultSearchFn = useCallback((item, term) => {
    return displayFn(item).toLowerCase().includes(term);
  }, [displayFn]);

  const effectiveSearchFn = searchFn || defaultSearchFn;

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(item => effectiveSearchFn(item, q));
  }, [items, search, effectiveSearchFn]);

  const groups = useMemo(() => {
    if (!groupFn) return null;
    const map = new Map();
    filtered.forEach(item => {
      const key = groupFn(item);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    });
    return Array.from(map.entries()).map(([label, groupItems]) => ({ label, items: groupItems }));
  }, [filtered, groupFn]);

  const toggle = useCallback((itemValue) => {
    const next = value.includes(itemValue)
      ? value.filter(v => v !== itemValue)
      : [...value, itemValue];
    onChange(next);
  }, [value, onChange]);

  const clearAll = useCallback(() => onChange([]), [onChange]);

  const selectedDisplayNames = useMemo(() => {
    const map = {};
    items.forEach(item => { map[valueFn(item)] = displayFn(item); });
    return value.map(v => ({ value: v, label: map[v] || String(v) }));
  }, [items, value, valueFn, displayFn]);

  const label = useMemo(() => {
    if (loading) return loadingLabel;
    if (value.length === 0) return allLabel || placeholder;
    if (value.length === 1) return selectedDisplayNames[0]?.label || String(value[0]);
    if (value.length <= 2) return selectedDisplayNames.map(s => s.label).join(', ');
    return `${value.length} selected`;
  }, [value, allLabel, placeholder, loading, loadingLabel, selectedDisplayNames]);

  const toggleGroup = (groupLabel) => {
    setExpandedGroups(prev => ({ ...prev, [groupLabel]: prev[groupLabel] === false ? true : false }));
  };

  const focusRing = isAdmin
    ? 'focus:ring-red-500 focus:border-red-500'
    : 'focus:ring-blue-500 focus:border-blue-500';

  const checkboxColor = isAdmin
    ? 'accent-red-600 text-red-600 focus-visible:ring-red-500'
    : 'accent-blue-700 text-blue-700 focus-visible:ring-blue-500';

  const chipColor = isAdmin
    ? 'bg-red-50 text-red-800 ring-red-600/20'
    : 'bg-blue-50 text-blue-800 ring-blue-600/20';

  const isFullWidth = className.includes('w-full');

  const triggerClasses = isPill
    ? `flex items-center gap-1.5 bg-white border border-gray-200 h-11 px-4 rounded-full shadow-sm hover:border-gray-300 transition-colors ${minWidth} focus:outline-none focus:ring-2 ${focusRing} text-sm font-medium ${value.length === 0 ? 'text-gray-400' : 'text-gray-900'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`
    : `w-full text-left flex items-center justify-between h-11 px-4 border border-gray-300 rounded-full shadow-sm bg-white focus:outline-none focus:ring-2 ${focusRing} transition-[border-color,box-shadow] duration-200 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'cursor-pointer'} ${className}`;

  const renderCheckboxItem = (item) => {
    const itemValue = valueFn(item);
    const checked = value.includes(itemValue);
    return (
      <label
        key={itemValue}
        className={`flex items-center gap-2 px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm ${checked ? (isAdmin ? 'bg-red-50' : 'bg-blue-50') : ''}`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => toggle(itemValue)}
          className={`rounded border-gray-300 ${checkboxColor}`}
        />
        {renderItem ? renderItem(item, checked) : (
          <span className="flex-1 text-gray-700">{displayFn(item)}</span>
        )}
      </label>
    );
  };

  const menuBody = (
    <>
      <div className="p-2 border-b border-gray-100 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            ref={searchRef}
            type="text"
            className={`w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${isAdmin ? 'focus:ring-red-500' : 'focus:ring-blue-500'}`}
            placeholder={searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className={usePortal ? 'overflow-y-auto min-h-0 flex-1' : 'overflow-y-auto max-h-60'}>
        {allLabel && !search && (
          <label className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
            <input
              type="checkbox"
              checked={value.length === 0}
              onChange={clearAll}
              className={`rounded border-gray-300 ${checkboxColor}`}
            />
            <span className="text-sm font-medium text-gray-900">{allLabel}</span>
          </label>
        )}

        {groups ? (
          groups.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">{emptyLabel}</div>
          ) : (
            groups.map(group => (
              <div key={group.label}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.label)}
                  className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100 text-left"
                >
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {group.label} ({group.items.length})
                  </span>
                  {expandedGroups[group.label] === false ? (
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                  ) : (
                    <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </button>
                {expandedGroups[group.label] !== false && group.items.map(renderCheckboxItem)}
              </div>
            ))
          )
        ) : (
          filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">{emptyLabel}</div>
          ) : (
            filtered.map(renderCheckboxItem)
          )
        )}
      </div>

      {value.length > 0 && (
        <div className="border-t border-gray-100 px-3 py-2 flex items-center justify-between bg-gray-50 flex-shrink-0">
          <span className="text-xs text-gray-500">{value.length} selected</span>
          <button type="button" onClick={clearAll} className={`text-xs ${isAdmin ? 'text-red-600 hover:text-red-700' : 'text-blue-700 hover:text-blue-800'} hover:underline`}>
            Clear all
          </button>
        </div>
      )}
    </>
  );

  const inlineMenu = isOpen && !usePortal && (
    <div className={`absolute ${isPill ? 'left-0 min-w-full' : 'left-0 right-0'} z-[9999] mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-hidden`}>
      {menuBody}
    </div>
  );

  const portaledMenu = isOpen && usePortal && typeof document !== 'undefined' && createPortal(
    <div
      ref={menuRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden flex flex-col"
    >
      {menuBody}
    </div>,
    document.body,
  );

  return (
    <div className={`relative ${isFullWidth && isPill ? 'w-full' : ''}`} ref={ref}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        disabled={disabled}
        className={triggerClasses}
      >
        {isPill && Icon && <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0`} />}
        {!isPill && Icon && <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0 mr-2`} />}
        <span className={`truncate ${isPill ? 'flex-1 text-left' : 'flex-1'}`}>{label}</span>
        <ChevronDown
          className={`${isPill ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-gray-500 flex-shrink-0 ${isPill ? '' : 'ml-2'} transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {inlineMenu}
      {portaledMenu}

      {showChips && value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedDisplayNames.map(({ value: v, label: l }) => (
            <span key={v} className={`inline-flex items-center gap-1 px-2 py-0.5 ${chipColor} text-xs rounded-full ring-1 ring-inset`}>
              {l}
              <button type="button" onClick={() => toggle(v)} className="hover:opacity-75">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableMultiCombobox;
